import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Heart, MagnifyingGlass, SignOut, User, ShieldStar, List, X } from "@phosphor-icons/react";

export default function Layout({ children, hideNav = false }) {
  const { user, logout } = useAuth() || {};
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const closeMobile = () => setMobileOpen(false);

  // Prevent body scroll when drawer is open
  React.useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <div className="min-h-screen relative">
      {/* Decorative blobs */}
      <div className="blob bg-rose-200" style={{ width: 380, height: 380, top: -80, left: -80 }} />
      <div className="blob bg-orange-200" style={{ width: 320, height: 320, top: 200, right: -100 }} />

      {!hideNav && (
        <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-rose-100/60">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-28 flex items-center justify-between">
            <Link to="/" data-testid="logo-home-link">
              <Logo size={88} />
            </Link>
            <nav className="hidden md:flex items-center gap-1 lg:gap-2">
              <Link to="/trouve" data-testid="nav-found-link">
                <Button variant="outline" className="rounded-full border-2 border-rose-300 text-rose-700 hover:bg-rose-50 font-bold text-sm px-3 lg:px-4 whitespace-nowrap">
                  <MagnifyingGlass size={16} weight="bold" className="mr-1.5" /> J'ai trouvé un doudou
                </Button>
              </Link>
              <Link to="/doudous-trouves" className="px-2 lg:px-3 py-2 text-stone-700 hover:text-orange-600 font-semibold text-sm whitespace-nowrap" data-testid="nav-found-reports-link">
                Carte des doudous signalés
              </Link>
              {user ? (
                <>
                  <Link to="/dashboard" className="px-2 lg:px-3 py-2 text-stone-700 hover:text-orange-600 font-semibold text-sm flex items-center gap-1.5 whitespace-nowrap" data-testid="nav-dashboard-link">
                    <Heart size={18} weight="duotone" /> Mes doudous
                  </Link>
                  <Link to="/conversations" className="px-2 lg:px-3 py-2 text-stone-700 hover:text-orange-600 font-semibold text-sm whitespace-nowrap" data-testid="nav-conversations-link">
                    Messages
                  </Link>
                  <Link to="/profil" className="px-2 lg:px-3 py-2 text-stone-700 hover:text-orange-600 font-semibold text-sm flex items-center gap-1.5 whitespace-nowrap" data-testid="nav-profile-link">
                    <User size={18} weight="duotone" /> {user.name?.split(" ")[0]}
                  </Link>
                  {user.is_admin && (
                    <Link to="/admin" className="px-3 py-2 text-orange-600 hover:text-orange-700 font-bold flex items-center gap-1" data-testid="nav-admin-link" title="Admin">
                      <ShieldStar size={20} weight="duotone" />
                    </Link>
                  )}
                  <button
                    onClick={async () => { await logout(); navigate("/"); }}
                    className="p-2 text-stone-500 hover:text-rose-600"
                    data-testid="nav-logout-button"
                    title="Se déconnecter"
                  >
                    <SignOut size={20} />
                  </button>
                </>
              ) : (
                <>
                  <Link to="/connexion" data-testid="nav-login-link">
                    <Button variant="ghost" className="rounded-full">Connexion</Button>
                  </Link>
                  <Link to="/inscription" data-testid="nav-signup-link">
                    <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-6 shadow-lg shadow-orange-500/30">
                      Créer un compte
                    </Button>
                  </Link>
                </>
              )}
            </nav>
            {/* Mobile: hamburger menu */}
            <div className="md:hidden">
              <button
                onClick={() => setMobileOpen(true)}
                className="p-3 rounded-full bg-rose-50 text-rose-600 hover:bg-rose-100"
                data-testid="mobile-menu-button"
                aria-label="Ouvrir le menu"
              >
                <List size={22} weight="bold" />
              </button>
            </div>
          </div>
        </header>
      )}

      {/* Mobile drawer (rendered OUTSIDE <header> to escape its backdrop-filter containing block) */}
      {!hideNav && mobileOpen && (
        <div className="md:hidden fixed inset-0 z-[9999] bg-stone-900/60 backdrop-blur-sm" onClick={closeMobile} data-testid="mobile-drawer">
          <div
            className="fixed top-0 right-0 bottom-0 w-[85%] max-w-xs bg-white shadow-2xl flex flex-col overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ paddingTop: 16, paddingBottom: 24, paddingLeft: 16, paddingRight: 16 }}
          >
            <div className="flex items-center justify-between mb-4">
              <span className="font-display text-lg font-black text-stone-800">Menu</span>
              <button onClick={closeMobile} className="p-2 text-stone-500 hover:text-rose-600" data-testid="mobile-menu-close" aria-label="Fermer le menu">
                <X size={22} weight="bold" />
              </button>
            </div>
            <div className="flex flex-col gap-2">
              <Link to="/trouve" onClick={closeMobile} className="px-4 py-3 rounded-2xl bg-rose-50 text-rose-700 font-bold flex items-center gap-2" data-testid="mobile-nav-found">
                <MagnifyingGlass size={18} weight="bold" /> J'ai trouvé un doudou
              </Link>
              <Link to="/j-ai-perdu-un-doudou" onClick={closeMobile} className="px-4 py-3 rounded-2xl bg-orange-50 text-orange-700 font-bold flex items-center gap-2" data-testid="mobile-nav-lost">
                <Heart size={18} weight="bold" /> J'ai perdu un doudou
              </Link>
              <Link to="/doudous-trouves" onClick={closeMobile} className="px-4 py-3 rounded-2xl text-stone-700 hover:bg-rose-50 font-semibold" data-testid="mobile-nav-found-reports">
                Carte des doudous signalés
              </Link>
              {user ? (
                <>
                  <Link to="/dashboard" onClick={closeMobile} className="px-4 py-3 rounded-2xl text-stone-700 hover:bg-rose-50 font-semibold flex items-center gap-2" data-testid="mobile-nav-dashboard">
                    <Heart size={18} weight="duotone" /> Mes doudous
                  </Link>
                  <Link to="/conversations" onClick={closeMobile} className="px-4 py-3 rounded-2xl text-stone-700 hover:bg-rose-50 font-semibold" data-testid="mobile-nav-conversations">
                    Messages
                  </Link>
                  <Link to="/profil" onClick={closeMobile} className="px-4 py-3 rounded-2xl text-stone-700 hover:bg-rose-50 font-semibold flex items-center gap-2" data-testid="mobile-nav-profile">
                    <User size={18} weight="duotone" /> Mon profil
                  </Link>
                  {user.is_admin && (
                    <Link to="/admin" onClick={closeMobile} className="px-4 py-3 rounded-2xl text-orange-700 hover:bg-orange-50 font-semibold flex items-center gap-2" data-testid="mobile-nav-admin">
                      <ShieldStar size={18} weight="duotone" /> Admin
                    </Link>
                  )}
                  <button onClick={async () => { closeMobile(); await logout(); navigate("/"); }} className="mt-2 px-4 py-3 rounded-2xl text-stone-500 hover:bg-rose-50 font-semibold flex items-center gap-2 text-left" data-testid="mobile-nav-logout">
                    <SignOut size={18} weight="bold" /> Se déconnecter
                  </button>
                </>
              ) : (
                <>
                  <Link to="/connexion" onClick={closeMobile} className="px-4 py-3 rounded-2xl text-stone-700 hover:bg-rose-50 font-semibold" data-testid="mobile-nav-login">
                    Connexion
                  </Link>
                  <Link to="/inscription" onClick={closeMobile} className="px-4 py-3 rounded-2xl bg-orange-500 text-white font-bold text-center" data-testid="mobile-nav-signup">
                    Créer un compte
                  </Link>
                </>
              )}
            </div>
            <div className="mt-6 pt-4 border-t border-rose-100 flex flex-col gap-1 text-sm text-stone-500">
              <Link to="/a-propos" onClick={closeMobile} className="py-2 px-4">À propos</Link>
              <Link to="/blog" onClick={closeMobile} className="py-2 px-4">Blog</Link>
              <Link to="/faq" onClick={closeMobile} className="py-2 px-4">FAQ</Link>
              <Link to="/contact" onClick={closeMobile} className="py-2 px-4">Contact</Link>
            </div>
          </div>
        </div>
      )}

      <main className="relative z-10">{children}</main>

      <footer className="relative z-10 mt-24 border-t border-rose-100/60 bg-white/40 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <Logo size={36} />
          <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
            <Link to="/a-propos" className="text-stone-500 hover:text-orange-600 font-medium" data-testid="footer-about-link">À propos</Link>
            <Link to="/blog" className="text-stone-500 hover:text-orange-600 font-medium" data-testid="footer-blog-link">Blog</Link>
            <Link to="/faq" className="text-stone-500 hover:text-orange-600 font-medium" data-testid="footer-faq-link">FAQ</Link>
            <Link to="/cgu" className="text-stone-500 hover:text-orange-600 font-medium" data-testid="footer-cgu-link">CGU</Link>
            <Link to="/confidentialite" className="text-stone-500 hover:text-orange-600 font-medium" data-testid="footer-privacy-link">Confidentialité</Link>
            <Link to="/mentions-legales" className="text-stone-500 hover:text-orange-600 font-medium" data-testid="footer-legal-link">Mentions légales</Link>
            <Link to="/contact" className="text-stone-500 hover:text-orange-600 font-medium" data-testid="footer-contact-link">Contact</Link>
          </nav>
          <p className="text-sm text-stone-500">Fait avec <span className="text-rose-500">♥</span> · © 2026 Doudou Alert</p>
        </div>
      </footer>
    </div>
  );
}
